import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Power, Users, Shield } from 'lucide-react'
import type { AreaListItem } from '@/modules/users/types/area'

interface AreasTableProps {
  areas: AreaListItem[]
  onEdit: (areaId: number) => void
  onDelete: (areaId: number) => void
  onToggleActive: (areaId: number) => void
  onManageUsers?: (areaId: number, areaName: string) => void
  onManageGroups?: (areaId: number, areaName: string) => void
}

export function AreasTable({ areas, onEdit, onDelete, onToggleActive, onManageUsers, onManageGroups }: AreasTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
          <TableHead>Nombre</TableHead>
          <TableHead>Laboratorio</TableHead>
          <TableHead className="text-center">Grupos</TableHead>
          <TableHead className="text-center">Usuarios</TableHead>
          <TableHead className="text-center">Orden</TableHead>
          <TableHead className="text-center">Estado</TableHead>
          <TableHead className="w-[60px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {areas.map((area) => (
          <TableRow key={area.id} className="hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
            <TableCell>
              <div>
                <div className="font-medium">{area.nombre}</div>
                {area.descripcion && (
                  <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                    {area.descripcion}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              {area.laboratorio_nombre ? (
                <div className="flex items-center gap-2">
                  {area.color_nombre && (
                    <div
                      className={`h-4 w-4 rounded-full border border-gray-300 dark:border-gray-600 shrink-0 bg-${area.color_nombre}-${area.color_tono || '500'}`}
                    />
                  )}
                  <Badge variant="outline" className="badge-gradient">
                    {area.laboratorio_nombre}
                  </Badge>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">Sin laboratorio</span>
              )}
            </TableCell>
            <TableCell className="text-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-teal-600 hover:text-teal-700 hover:bg-teal-500/10"
                onClick={() => onManageGroups?.(area.id, area.nombre)}
              >
                <Shield className="h-3.5 w-3.5" />
                <span>{area.grupos_count}</span>
              </Button>
            </TableCell>
            <TableCell className="text-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-teal-600 hover:text-teal-700 hover:bg-teal-500/10"
                onClick={() => onManageUsers?.(area.id, area.nombre)}
              >
                <Users className="h-3.5 w-3.5" />
                <span>{area.usuarios_count}</span>
              </Button>
            </TableCell>
            <TableCell className="text-center text-sm">{area.orden}</TableCell>
            <TableCell className="text-center">
              <Badge variant={area.is_active ? 'default' : 'secondary'}>
                {area.is_active ? 'Activa' : 'Inactiva'}
              </Badge>
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(area.id)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggleActive(area.id)}>
                    <Power className="mr-2 h-4 w-4" />
                    {area.is_active ? 'Desactivar' : 'Activar'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(area.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
